/**
 * SCRUM-410 — "Recursos" pasa de compartidor de archivos simple (crear/borrar,
 * sin editar) a producto digital liviano con precio/tipo/vencimiento y
 * entregas trazables (link con token, resend, revocar, regenerar).
 * No usa APIs nativas de confirm/alert — todo pasa por AppDialogs.
 *
 * Deliberadamente NO incluido en esta pasada: la compra self-service con pago
 * desde el widget del cliente. Acá el flujo es "el negocio da de alta el
 * recurso y otorga/comparte el acceso" (igual a como ya funcionaba, pero con
 * traza y control). Cobrar automáticamente en el checkout es un cambio en
 * código de pagos que merece su propia pasada revisada aparte.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { CrudShellHeader, CrudEmptyState, Loading } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { apiGetJson, apiSendJson, apiDelete, clientRequestHeaders } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useAppDialogs } from "@/components/ui/app-dialogs";
import { toast } from "sonner";
import {
  FileText,
  Loader2,
  Pencil,
  Trash2,
  Upload,
  Link2,
  Mail,
  RefreshCw,
  Ban,
  Plus,
} from "lucide-react";
import type { ClientResource, ClientResourceDelivery, ClientResourceType } from "@/lib/db-service/service-resources";

export const Route = createFileRoute("/app/recursos")({
  component: Page,
  head: () => ({ meta: [{ title: "Recursos" }] }),
});

const TYPE_LABEL: Record<ClientResourceType, string> = {
  pdf: "PDF",
  video: "Video",
  audio: "Audio",
  link: "Link",
  other: "Otro",
};

const DELIVERY_STATUS_LABEL: Record<string, string> = {
  active: "Activo",
  revoked: "Revocado",
};

type FormState = {
  title: string;
  description: string;
  resourceType: ClientResourceType;
  price: string;
  fileUrl: string;
  fileName: string | null;
  storageKey: string | null;
  coverImageUrl: string;
  expiresAfterDays: string;
};

const EMPTY_FORM: FormState = {
  title: "",
  description: "",
  resourceType: "pdf",
  price: "",
  fileUrl: "",
  fileName: null,
  storageKey: null,
  coverImageUrl: "",
  expiresAfterDays: "",
};

function Page() {
  const tenantId = useWorkspaceTenantId();
  const { confirm } = useAppDialogs();
  const [loading, setLoading] = useState(true);
  const [resources, setResources] = useState<ClientResource[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);

  const [deliveriesFor, setDeliveriesFor] = useState<ClientResource | null>(null);
  const [deliveries, setDeliveries] = useState<ClientResourceDelivery[]>([]);
  const [deliveriesLoading, setDeliveriesLoading] = useState(false);
  const [grantEmail, setGrantEmail] = useState("");
  const [granting, setGranting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGetJson<{ items: ClientResource[] }>("/api/service-admin?resource=resources", tenantId);
      setResources(data.items ?? []);
    } catch {
      toast.error("No se pudieron cargar los recursos");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  function startCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setCreating(true);
  }

  function startEdit(r: ClientResource) {
    setCreating(false);
    setEditingId(r.id);
    setForm({
      title: r.title,
      description: r.description ?? "",
      resourceType: r.resourceType ?? "pdf",
      price: r.price != null ? String(r.price) : "",
      fileUrl: r.fileUrl,
      fileName: r.fileName,
      storageKey: r.storageKey,
      coverImageUrl: r.coverImageUrl ?? "",
      expiresAfterDays: r.expiresAfterDays != null ? String(r.expiresAfterDays) : "",
    });
  }

  function cancelForm() {
    setCreating(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  async function uploadFile(file: File) {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("resourceId", editingId ?? `tmp-${Date.now()}`);
      fd.append("file", file);
      const res = await fetch("/api/upload/client-resource", {
        method: "POST",
        headers: clientRequestHeaders(tenantId),
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Upload falló");
      setForm((f) => ({ ...f, storageKey: data.storageKey, fileName: data.fileName, fileUrl: f.fileUrl || data.fileName }));
      toast.success("Archivo subido");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al subir el archivo");
    } finally {
      setUploading(false);
    }
  }

  async function submitForm() {
    if (!form.title.trim()) {
      toast.error("El título es obligatorio");
      return;
    }
    if (!form.storageKey && !form.fileUrl.trim()) {
      toast.error("Subí un archivo o indicá una URL");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || null,
        resourceType: form.resourceType,
        price: form.price.trim() ? Number(form.price) : null,
        fileUrl: form.fileUrl.trim() || form.fileName || "",
        fileName: form.fileName,
        storageKey: form.storageKey,
        coverImageUrl: form.coverImageUrl.trim() || null,
        expiresAfterDays: form.expiresAfterDays.trim() ? Number(form.expiresAfterDays) : null,
      };
      if (editingId) {
        await apiSendJson(`/api/service-admin?resource=resources&id=${editingId}`, "PATCH", payload, tenantId);
        toast.success("Recurso actualizado");
      } else {
        await apiSendJson("/api/service-admin?resource=resources", "POST", payload, tenantId);
        toast.success("Recurso creado");
      }
      cancelForm();
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(r: ClientResource) {
    try {
      await apiSendJson(`/api/service-admin?resource=resources&id=${r.id}`, "PATCH", { active: !r.active }, tenantId);
      void load();
    } catch {
      toast.error("No se pudo actualizar");
    }
  }

  async function removeResource(r: ClientResource) {
    const ok = await confirm({
      title: "Eliminar recurso",
      description: `Se va a eliminar "${r.title}" y sus accesos otorgados dejarán de funcionar. Esta acción no se puede deshacer.`,
      destructive: true,
    });
    if (!ok) return;
    try {
      await apiDelete(`/api/service-admin?resource=resources&id=${r.id}`, tenantId);
      toast.success("Recurso eliminado");
      if (deliveriesFor?.id === r.id) setDeliveriesFor(null);
      void load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  }

  const loadDeliveries = useCallback(
    async (resourceId: string) => {
      setDeliveriesLoading(true);
      try {
        const data = await apiGetJson<{ items: ClientResourceDelivery[] }>(
          `/api/service-admin?resource=resource-deliveries&resourceId=${encodeURIComponent(resourceId)}`,
          tenantId,
        );
        setDeliveries(data.items ?? []);
      } catch {
        toast.error("No se pudieron cargar los accesos");
      } finally {
        setDeliveriesLoading(false);
      }
    },
    [tenantId],
  );

  function openDeliveries(r: ClientResource) {
    setDeliveriesFor(r);
    setGrantEmail("");
    void loadDeliveries(r.id);
  }

  async function grantAccess() {
    if (!deliveriesFor || !grantEmail.trim()) return;
    setGranting(true);
    try {
      await apiSendJson(
        "/api/service-admin?resource=resource-deliveries",
        "POST",
        { action: "create", resourceId: deliveriesFor.id, customerEmail: grantEmail.trim() },
        tenantId,
      );
      toast.success("Acceso otorgado — se envió el mail con el link");
      setGrantEmail("");
      void loadDeliveries(deliveriesFor.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo otorgar el acceso");
    } finally {
      setGranting(false);
    }
  }

  async function deliveryAction(d: ClientResourceDelivery, action: "revoke" | "regenerate" | "resend") {
    if (!deliveriesFor) return;
    if (action === "revoke") {
      const ok = await confirm({
        title: "Revocar acceso",
        description: `${d.customerEmail} ya no va a poder descargar "${deliveriesFor.title}" con este link.`,
        destructive: true,
      });
      if (!ok) return;
    }
    try {
      await apiSendJson("/api/service-admin?resource=resource-deliveries", "POST", { action, id: d.id }, tenantId);
      toast.success(
        action === "revoke" ? "Acceso revocado" : action === "regenerate" ? "Link regenerado y reenviado" : "Mail reenviado",
      );
      void loadDeliveries(deliveriesFor.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo completar la acción");
    }
  }

  return (
    <div className="flex h-full flex-col">
      <MerchantExperienceBar screenId="recursos" />
      <CrudShellHeader
        title="Recursos"
        description="Archivos y contenido digital para tus clientes: compartilo gratis o vendelo con acceso trazable."
        count={resources.length}
        action={
          <Button onClick={startCreate} size="sm">
            <Plus className="mr-2 h-4 w-4" />
            Nuevo recurso
          </Button>
        }
      />

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {(creating || editingId) && (
          <div className="space-y-4 rounded-xl border border-border p-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <FileText className="h-4 w-4" />
              {editingId ? "Editar recurso" : "Nuevo recurso"}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Título</Label>
                <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Ej: Guía de cuidados post-tratamiento" />
              </div>
              <div>
                <Label>Tipo</Label>
                <Select value={form.resourceType} onValueChange={(v) => setForm((f) => ({ ...f, resourceType: v as ClientResourceType }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label>Descripción (opcional)</Label>
                <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} />
              </div>
              <div>
                <Label>Precio (opcional — vacío = solo compartir, sin venta)</Label>
                <Input type="number" min={0} value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} placeholder="0" />
              </div>
              <div>
                <Label>Vence a los días de entregado (opcional)</Label>
                <Input type="number" min={1} value={form.expiresAfterDays} onChange={(e) => setForm((f) => ({ ...f, expiresAfterDays: e.target.value }))} placeholder="Sin vencimiento" />
              </div>
              <div>
                <Label>Imagen de portada — URL (opcional)</Label>
                <Input value={form.coverImageUrl} onChange={(e) => setForm((f) => ({ ...f, coverImageUrl: e.target.value }))} placeholder="https://..." />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>Archivo</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    type="file"
                    className="max-w-xs"
                    accept=".pdf,.zip,video/mp4,image/*,audio/*,text/plain"
                    disabled={uploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void uploadFile(f);
                    }}
                  />
                  {uploading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  {form.storageKey && (
                    <Badge variant="secondary" className="gap-1">
                      <Upload className="h-3 w-3" />
                      {form.fileName ?? "archivo subido"}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Link2 className="h-3.5 w-3.5" />
                  o pegá una URL externa (ej. link a un video ya alojado):
                </div>
                <Input
                  value={form.fileUrl}
                  onChange={(e) => setForm((f) => ({ ...f, fileUrl: e.target.value }))}
                  placeholder="https://..."
                  disabled={Boolean(form.storageKey)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => void submitForm()} disabled={saving}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {editingId ? "Guardar cambios" : "Crear recurso"}
              </Button>
              <Button variant="ghost" onClick={cancelForm}>Cancelar</Button>
            </div>
          </div>
        )}

        {loading ? (
          <Loading rows={3} />
        ) : resources.length === 0 ? (
          <CrudEmptyState icon={<FileText className="h-8 w-8" />} title="Todavía no hay recursos" description="Creá el primero para compartir o vender contenido digital a tus clientes." />
        ) : (
          <ul className="space-y-2">
            {resources.map((r) => (
              <li key={r.id} className="rounded-lg border border-border px-4 py-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 font-medium">
                      <span className="truncate">{r.title}</span>
                      <Badge variant="outline" className="shrink-0">{TYPE_LABEL[r.resourceType ?? "other"]}</Badge>
                      {!r.active && <Badge variant="secondary" className="shrink-0">Inactivo</Badge>}
                      {r.price != null && <Badge className="shrink-0">${r.price.toLocaleString("es-AR")}</Badge>}
                    </div>
                    {r.description && <p className="mt-0.5 truncate text-xs text-muted-foreground">{r.description}</p>}
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {r.expiresAfterDays != null ? `Vence a los ${r.expiresAfterDays} días de entregado` : "Sin vencimiento"}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Switch checked={r.active} onCheckedChange={() => void toggleActive(r)} />
                    <Button type="button" variant="ghost" size="icon" onClick={() => openDeliveries(r)} title="Compras y accesos">
                      <Mail className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => startEdit(r)} title="Editar">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => void removeResource(r)} title="Eliminar">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {deliveriesFor?.id === r.id && (
                  <div className="mt-3 space-y-3 border-t border-border/50 pt-3">
                    <div className="text-xs font-semibold text-muted-foreground">Compras y accesos</div>
                    <div className="flex flex-wrap gap-2">
                      <Input
                        value={grantEmail}
                        onChange={(e) => setGrantEmail(e.target.value)}
                        placeholder="email@cliente.com"
                        className="max-w-xs"
                      />
                      <Button type="button" size="sm" onClick={() => void grantAccess()} disabled={granting || !grantEmail.trim()}>
                        {granting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Dar acceso
                      </Button>
                    </div>
                    {deliveriesLoading ? (
                      <Loading rows={2} />
                    ) : deliveries.length === 0 ? (
                      <p className="text-xs text-muted-foreground">Todavía no se otorgó acceso a nadie.</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {deliveries.map((d) => (
                          <li key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-muted/40 px-3 py-1.5 text-xs">
                            <span className="flex items-center gap-2">
                              {d.customerEmail}
                              <Badge variant={d.status === "active" ? "secondary" : "outline"}>{DELIVERY_STATUS_LABEL[d.status]}</Badge>
                              <span className="text-muted-foreground">{d.downloadCount} descarga{d.downloadCount === 1 ? "" : "s"}</span>
                              {d.expiresAt && <span className="text-muted-foreground">vence {new Date(d.expiresAt).toLocaleDateString("es-AR")}</span>}
                            </span>
                            <span className="flex items-center gap-1">
                              <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => void deliveryAction(d, "resend")} title="Reenviar mail">
                                <Mail className="h-3.5 w-3.5" />
                              </Button>
                              <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => void deliveryAction(d, "regenerate")} title="Regenerar link">
                                <RefreshCw className="h-3.5 w-3.5" />
                              </Button>
                              {d.status === "active" && (
                                <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => void deliveryAction(d, "revoke")} title="Revocar">
                                  <Ban className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
